import { useState } from 'react';

type ServiceCategory = 'startup' | 'management' | 'cleaning' | 'tax';

export default function ServicesSection() {
  const [activeTab, setActiveTab] = useState<ServiceCategory>('startup');

  const services = {
    startup: {
      title: '開業サポート',
      icon: 'ri-rocket-line',
      description: '簡易宿所・民泊の開業に必要な申請手続きから保健所・消防対応まで、実務経験に基づいた確実なサポートを提供します。',
      items: [
        {
          icon: 'ri-file-list-3-line',
          title: '簡易宿所申請サポート',
          description: '保健所への申請書類作成から提出まで、実務経験に基づいた確実なサポートを提供します。東京都内での実績多数。'
        },
        {
          icon: 'ri-home-4-line',
          title: '民泊届出サポート',
          description: '住宅宿泊事業法に基づく届出手続きを、スムーズに完了できるようサポートします。必要書類の準備から提出まで代行可能。'
        },
        {
          icon: 'ri-hospital-line',
          title: '保健所対応アドバイス',
          description: '保健所との折衝や指摘事項への対応方法を、実例をもとにアドバイスします。事前相談から検査立会いまで対応。'
        },
        {
          icon: 'ri-fire-line',
          title: '消防対応アドバイス',
          description: '消防設備の設置基準や検査対応について、具体的な指導を行います。消防署との折衝もサポート。'
        }
      ]
    },
    management: {
      title: '運営代行',
      icon: 'ri-settings-3-line',
      description: 'Airbnbリスティング作成から価格戦略、ゲスト対応まで、収益を最大化する運営代行サービスを提供します。',
      items: [
        {
          icon: 'ri-airplay-line',
          title: 'Airbnbページ作成',
          description: '予約率を高める魅力的なリスティング作成と、効果的な写真撮影のアドバイスを提供します。プロカメラマン手配も可能。'
        },
        {
          icon: 'ri-line-chart-line',
          title: '価格戦略',
          description: 'ダイナミックプライシングを活用し、稼働率と収益を最大化する価格設定を行います。市場分析に基づいた戦略的な価格調整。'
        },
        {
          icon: 'ri-customer-service-2-line',
          title: 'ゲスト対応',
          description: '予約受付からチェックイン・アウト、滞在中の問い合わせまで、多言語で丁寧に対応します。24時間対応可能。'
        },
        {
          icon: 'ri-star-line',
          title: 'レビュー改善',
          description: 'ゲストレビューの分析と改善提案を行い、評価を向上させます。高評価維持のための継続的なサポート。'
        }
      ]
    },
    cleaning: {
      title: '清掃管理',
      icon: 'ri-brush-line',
      description: 'チェックアウト後の清掃からリネン交換、品質管理まで、ゲストに快適な空間を提供するための清掃サービスを提供します。',
      items: [
        {
          icon: 'ri-home-smile-line',
          title: 'チェックアウト清掃',
          description: 'ゲスト退室後の清掃を迅速かつ丁寧に実施します。次のゲストを迎える準備を完璧に整えます。'
        },
        {
          icon: 'ri-t-shirt-line',
          title: 'リネン交換',
          description: 'シーツ・タオル・アメニティの交換と補充を行います。清潔で快適な宿泊環境を維持します。'
        },
        {
          icon: 'ri-shield-check-line',
          title: '品質管理',
          description: '清掃品質のチェックと写真報告を実施します。常に高水準の清潔さを保つための管理体制。'
        },
        {
          icon: 'ri-tools-line',
          title: '設備メンテナンス',
          description: '設備の不具合チェックと簡易修繕を行います。必要に応じて専門業者の手配も対応。'
        }
      ]
    },
    tax: {
      title: '税務サポート体制',
      icon: 'ri-calculator-line',
      description: '信頼できる会計事務所と連携し、民泊・簡易宿所運営に関わる税務サポート体制を整えています。',
      items: [
        {
          icon: 'ri-file-text-line',
          title: '確定申告サポート',
          description: '宿泊業特有の経費処理や青色申告の方法を、分かりやすく指導します。※税理士による代行ではなく、サポート・アドバイスとなります。'
        },
        {
          icon: 'ri-team-line',
          title: '会計事務所紹介',
          description: '民泊・簡易宿所の税務に詳しい信頼できる会計事務所をご紹介します。必要に応じて税理士との連携も可能。'
        },
        {
          icon: 'ri-money-dollar-circle-line',
          title: '経費管理アドバイス',
          description: '民泊運営で計上できる経費の種類や記帳方法について、実例をもとにアドバイスします。'
        },
        {
          icon: 'ri-question-answer-line',
          title: '税務相談サポート',
          description: '消費税や固定資産税など、民泊運営に関わる税務全般の相談に対応します。専門家への橋渡しも行います。'
        }
      ]
    }
  };

  const tabs: { key: ServiceCategory; label: string; icon: string }[] = [
    { key: 'startup', label: '開業サポート', icon: 'ri-rocket-line' },
    { key: 'management', label: '運営代行', icon: 'ri-settings-3-line' },
    { key: 'cleaning', label: '清掃管理', icon: 'ri-brush-line' },
    { key: 'tax', label: '税務サポート', icon: 'ri-calculator-line' }
  ];

  const currentService = services[activeTab];

  return (
    <section id="services" className="py-20 bg-white">
      <div className="container mx-auto px-6">
        <div className="max-w-6xl mx-auto">
          {/* セクションタイトル */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 text-emerald-700 text-sm font-bold px-5 py-2 rounded-full mb-6">
              <i className="ri-service-line text-base"></i>
              <span>Our Services</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-extrabold text-slate-900 mb-4">
              サービス内容
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              開業から運営、税務まで、民泊ビジネスに必要なすべてをワンストップでサポート
            </p>
          </div>

          {/* タブナビゲーション */}
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 px-6 py-3 rounded-lg font-bold text-sm transition-all whitespace-nowrap ${
                  activeTab === tab.key
                    ? 'bg-slate-900 text-white shadow-lg'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                <i className={`${tab.icon} text-lg`}></i>
                <span>{tab.label}</span>
              </button>
            ))}
          </div>

          {/* サービス詳細 */}
          <div className="bg-slate-50 rounded-2xl p-8 md:p-12">
            {/* カテゴリヘッダー */}
            <div className="flex items-start gap-4 mb-8 pb-8 border-b border-slate-200">
              <div className="w-16 h-16 flex items-center justify-center bg-slate-900 rounded-xl flex-shrink-0">
                <i className={`${currentService.icon} text-3xl text-white`}></i>
              </div>
              <div className="flex-1">
                <h3 className="text-3xl font-bold text-slate-900 mb-3">
                  {currentService.title}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {currentService.description}
                </p>
              </div>
            </div>

            {/* サービスアイテムグリッド */}
            <div className="grid md:grid-cols-2 gap-6">
              {currentService.items.map((item, index) => (
                <div
                  key={index}
                  className="bg-white p-6 rounded-xl border border-slate-200 hover:border-emerald-500/50 hover:shadow-lg transition-all"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-emerald-500 rounded-lg flex-shrink-0">
                      <i className={`${item.icon} text-2xl text-white`}></i>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-lg font-bold text-slate-900 mb-2">
                        {item.title}
                      </h4>
                      <p className="text-slate-600 text-sm leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* 税務サポートの注記 */}
            {activeTab === 'tax' && (
              <div className="mt-8 bg-amber-50 border border-amber-200 rounded-xl p-6">
                <div className="flex gap-3">
                  <i className="ri-information-line text-amber-600 text-xl flex-shrink-0 mt-1"></i>
                  <div>
                    <p className="text-slate-700 leading-relaxed">
                      <strong className="text-slate-900">税務サポートについて：</strong>
                      当社は税理士資格を持たないため、税務代行は行っておりません。確定申告のサポート・アドバイス、および信頼できる会計事務所のご紹介を通じて、税務面でのサポート体制を整えています。
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* CTA */}
          <div className="text-center mt-12">
            <a
              href="#contact"
              className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white font-bold px-8 py-4 rounded-lg shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all whitespace-nowrap"
            >
              <i className="ri-mail-line text-xl"></i>
              <span>サービスについて相談する</span>
              <i className="ri-arrow-right-line"></i>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}